/**
 * Tooltips & popovers - auto-initialized, dynamic content included.
 *
 * No manual init anywhere: instances are created LAZILY on the first
 * mouseover/focusin (tooltips) or click (popovers), so elements rendered
 * later (lister rows, dialogs, anything) just work.
 *
 *   - any element with a `title` (or data-bs-toggle="tooltip") gets the
 *     themed Bootstrap tooltip instead of the native browser one;
 *     opt out with `data-no-tooltip` (on the element or a container)
 *   - popovers stay explicit: data-bs-toggle="popover" + data-bs-* options
 *
 * The triggers are driven MANUALLY (hover + KEYBOARD focus only): with
 * Bootstrap's default 'hover focus' pair a CLICK leaves the button
 * focused, so the tooltip survived mouseout and sat there until the user
 * clicked elsewhere. Showing on focus only when it is `:focus-visible`
 * keeps the keyboard a11y and kills the stuck-after-click case - a mouse
 * click never counts as a reason to hold a tooltip open.
 */
export function initTooltip() {
	if (!window.bootstrap) return;

	const lazyTooltip = (e) => {
		if (!(e.target instanceof Element)) return;
		const el = e.target.closest('[title], [data-bs-toggle="tooltip"]');
		if (!el || el._lazyTip) return;
		el._lazyTip = true;
		if (el.closest('[data-no-tooltip]')) return;
		if (!(el.getAttribute('title') || '').trim() && !(el.getAttribute('data-bs-title') || '').trim()) return;
		if (el.tagName === 'IFRAME' || el.tagName === 'SELECT' || el.tagName === 'OPTION') return;
		const tip = new window.bootstrap.Tooltip(el, { container: 'body', trigger: 'manual' });
		let hover = false;
		let kbFocus = false;
		const sync = () => ((hover || kbFocus) ? tip.show() : tip.hide());
		// The four listeners belong to THIS instance and die WITH it (the
		// abort in dispose below): DOM listeners survive a detach/re-attach,
		// so without the signal a re-attached trigger kept the old closures
		// alive next to the fresh init's — and the stale sync() called
		// show() on the DISPOSED instance, an uncaught null-read on every
		// re-hover (found by the vux #43 round's pageerror tracking).
		const abort = new AbortController();
		el._lazyTipAbort = abort;
		const opts = { signal: abort.signal };
		el.addEventListener('mouseenter', () => { hover = true; sync(); }, opts);
		el.addEventListener('mouseleave', () => { hover = false; sync(); }, opts);
		el.addEventListener('focus', () => { kbFocus = el.matches(':focus-visible'); sync(); }, opts);
		el.addEventListener('blur', () => { kbFocus = false; sync(); }, opts);
		// The two lazy roads end differently (request #37 — the
		// first-hover-vanishes bug). POINTER: the spec fires the element's
		// own mouseenter right AFTER the document mouseover this init runs
		// in, so the freshly attached listener catches the SAME entry — do
		// NOT seed hover here: seeding made TWO show() calls for one entry,
		// and Bootstrap answers a mid-fade second show() with a self-hide
		// ~10ms after shown. KEYBOARD: the mirror image — focus has ALREADY
		// fired when the document focusin runs, the new listener missed it,
		// so this road IS seeded.
		if (e.type === 'focusin') {
			kbFocus = el.matches(':focus-visible');
			sync();
		}
	};

	document.addEventListener('mouseover', lazyTooltip);
	document.addEventListener('focusin', lazyTooltip);

	document.addEventListener('click', (e) => {
		if (!(e.target instanceof Element)) return;
		const el = e.target.closest('[data-bs-toggle="popover"]');
		if (!el || el._lazyPop) return;
		el._lazyPop = true;
		new window.bootstrap.Popover(el).show();
	});

	// The DISPOSAL half of the lazy lifecycle. Hide is driven by trigger
	// events (mouseleave/blur), so a trigger REMOVED from the DOM while its
	// tip was shown could never hide it - the tip node sat in <body> forever
	// (a lister reload() under the pointer, a deleted gallery tile...). One
	// observer disposes every initialized trigger that leaves the document;
	// the flag resets so a re-attached element lazily re-inits.
	//
	// NEVER dispose() while a show/hide transition is in flight (vux #43):
	// Bootstrap queues each fade's complete() on the TIP element - which
	// lives in <body>, outside the removed subtree, so it survives and
	// RUNS later - and dispose() nulls the instance's own state, so the
	// late callback reads Object.values(null) and throws (a mid-fade
	// removal happens on every re-render whose reflow first slides the
	// pointer off a hovered trigger). Waiting for `hidden` is NOT enough:
	// an INTERRUPTED fade (hover → unhover inside one ~150ms transition)
	// never gets its transitionend, so its emulated-duration fallback
	// timer stays pending and fires after `hidden` too. A live tip means
	// callbacks may be queued: start the hide now (its complete() removes
	// the tip node - still no orphan) and dispose only after every
	// possible fallback timer has fired. A late complete() on a live,
	// hidden instance is harmless; on a disposed one it throws.
	const disposeInstance = (instance, el) => {
		// dispose, then RE-ARM the nulled fields a straggler callback reads
		// (_isWithActiveTrigger, the aria cleanup on the element): an
		// interrupted fade's emulated-duration fallback timer can survive
		// with `tip` ALREADY null (rapid hover→unhover→hover queues a
		// complete whose transition was cancelled — the SECOND hide's
		// complete removes the tip, the first one's timer still fires), and
		// a CPU-starved page delays such timers past any fixed grace. With
		// the re-arm a late complete() is a no-op — never a throw.
		// (the tree lister's node collapse, Sep 2026: a trigger removed
		// DURING its show fade — Bootstrap's show-complete runs after the
		// dispose and, seeing _isHovered === false, calls _leave(), which
		// reads _config.delay on the nulled config. So: _isHovered stays
		// null (no _leave from a late show-complete) AND _config keeps a
		// delay object (a late _enter/_leave finds numbers, then hide() on
		// the disposed instance is a no-op — no tip).)
		const finish = () => {
			instance.dispose();
			instance._element = el;
			instance._activeTrigger = {};
			instance._isHovered = null;
			instance._config = { delay: { show: 0, hide: 0 } };
		};
		if (instance.tip) {
			instance.hide(); // no-op when the fade is already in flight
			// restore `title` NOW (dispose would, but it runs after the
			// grace): the element is detached, and if it re-attaches inside
			// the window the fresh lazy init must find its content — a
			// title-less re-init has nothing to show, ever
			if (!el.getAttribute('title') && el.getAttribute('data-bs-original-title')) {
				el.setAttribute('title', el.getAttribute('data-bs-original-title'));
			}
			// the grace lets the hide FINISH (its complete removes the tip
			// node — no orphan); > the ~155ms emulated-transition fallback
			setTimeout(() => {
				if (instance.constructor.getInstance(el) === instance) finish();
			}, 300);
		} else {
			finish();
		}
	};
	const dispose = (el) => {
		if (el._lazyTip) {
			el._lazyTipAbort?.abort(); // the instance's own listeners die with it
			delete el._lazyTipAbort;
			const tip = window.bootstrap.Tooltip.getInstance(el);
			if (tip) disposeInstance(tip, el);
			delete el._lazyTip;
		}
		if (el._lazyPop) {
			const pop = window.bootstrap.Popover.getInstance(el);
			if (pop) disposeInstance(pop, el);
			delete el._lazyPop;
		}
	};
	new MutationObserver((mutations) => {
		for (const m of mutations) {
			for (const node of m.removedNodes) {
				// a MOVE (drag reorder, appendChild elsewhere) re-attaches
				// before this async callback runs - only true removals count
				if (!(node instanceof Element) || node.isConnected) continue;
				dispose(node);
				// initialized tooltips carry data-bs-original-title (Bootstrap
				// moved the title there); popovers keep their toggle attribute
				for (const el of node.querySelectorAll('[data-bs-original-title], [data-bs-toggle="tooltip"], [data-bs-toggle="popover"]')) dispose(el);
			}
		}
	}).observe(document.documentElement, { childList: true, subtree: true });
}
